import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Loader2 } from "lucide-react";
import { API, api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const sessionId = () => {
  let id = localStorage.getItem("lg_chat_session");
  if (!id) {
    id = `s-${crypto.randomUUID()}`;
    localStorage.setItem("lg_chat_session", id);
  }
  return id;
};

const visible = (t) => t.split("[[")[0];
const GREETING = { role: "assistant", text: "¡Hola! Soy el asistente virtual de La Gringa. Elige una pregunta frecuente o escríbeme tu duda. También puedo ayudarte a agendar tu cita." };
const QUICK = ["Quiero agendar una cita", "Quiero una cotización"];

async function streamChat(body, onEvent) {
  const res = await fetch(`${API}/chat/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    parts.forEach((p) => p.startsWith("data: ") && onEvent(JSON.parse(p.slice(6))));
  }
}

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-green text-white" : m.kind === "ok" ? "bg-gold/20 text-ink border border-gold/50" : "bg-surface text-ink"}`}>
      {visible(m.text) || <Loader2 size={14} className="animate-spin" />}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const { site } = useSite() || {};
  const faqs = site?.faqs || [];
  const askFaq = (f) => setMsgs((m) => [...m, { role: "user", text: f.q }, { role: "assistant", text: f.a }]);

  useEffect(() => {
    if (!open) return;
    api.get(`/chat/history/${sessionId()}`).then((r) => r.data.length && setMsgs([GREETING, ...r.data])).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "" }]);
    const patchLast = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      await streamChat({ session_id: sessionId(), message: t }, (ev) => {
        if (ev.type === "delta" || ev.type === "error") patchLast((l) => ({ ...l, text: l.text + ev.content }));
        if (ev.type === "booking") {
          const txt = ev.ok ? `Cita registrada: ${ev.appointment.service} · ${ev.appointment.date} a las ${ev.appointment.time}. ¡Te esperamos!` : `No se pudo registrar: ${ev.error}`;
          setMsgs((m) => [...m, { role: "assistant", text: txt, kind: ev.ok ? "ok" : "" }]);
        }
        if (ev.type === "request" && ev.ok) setMsgs((m) => [...m, { role: "assistant", text: "Solicitud enviada al taller. Te contactaremos pronto.", kind: "ok" }]);
      });
    } catch {
      patchLast((l) => ({ ...l, text: "No hay conexión con el asistente. Escríbenos por WhatsApp al 999-191-9300." }));
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} aria-label={open ? "Cerrar asistente" : "Abrir asistente virtual"} aria-expanded={open}
        data-testid="chatbot-toggle-button"
        className="fixed bottom-5 left-5 z-50 flex h-14 items-center gap-2 rounded-full border-2 border-gold bg-green px-5 text-white shadow-xl shadow-black/20 transition-transform hover:scale-105">
        {open ? <X size={22} /> : <Bot size={22} />}<span className="hidden sm:inline font-display text-base font-bold">{open ? "Cerrar" : "¿Dudas? Pregúntanos"}</span>
      </button>
      {open && (
        <div role="dialog" aria-label="Asistente virtual" data-testid="chatbot-panel"
          className="rise fixed bottom-24 left-3 right-3 sm:left-5 sm:right-auto z-50 flex h-[70vh] max-h-[600px] sm:w-[390px] flex-col overflow-hidden rounded-2xl border border-line bg-white shadow-2xl shadow-black/20">
          <div className="flex items-center gap-3 bg-green px-4 py-3 text-white">
            <img src="/logo.png" alt="" className="h-9 w-9 rounded-full" />
            <div><p className="font-display text-base font-bold leading-none">Asistente La Gringa</p><p className="text-xs text-white/75">Preguntas frecuentes y citas</p></div>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite" data-testid="chatbot-messages">
            {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {!busy && (
              <div className="space-y-2 pt-2" data-testid="chat-faq-list">
                <p className="text-[11px] font-bold uppercase tracking-wider text-mute2">Preguntas frecuentes</p>
                <div className="flex flex-wrap gap-2">
                  {faqs.map((f, i) => <button key={f.id} onClick={() => askFaq(f)} className="rounded-full border border-line bg-white px-3 py-1.5 text-left text-xs text-green hover:border-gold" data-testid={`chat-faq-${i}`}>{f.q}</button>)}
                  {QUICK.map((q, i) => <button key={q} onClick={() => send(q)} className="rounded-full border border-gold bg-gold/10 px-3 py-1.5 text-xs font-semibold text-ink hover:bg-gold/20" data-testid={`chat-quick-${i}`}>{q}</button>)}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-line p-3">
            <label htmlFor="chat-input" className="sr-only">Escribe tu mensaje</label>
            <input id="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribe tu mensaje..." maxLength={1000}
              className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-brand" data-testid="chatbot-input" />
            <button type="submit" disabled={busy} aria-label="Enviar" className="btn-gold !px-3 !py-2" data-testid="chatbot-send-btn">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};
